import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Mail,
  Phone,
  Globe,
  Pencil,
  Check,
  Linkedin,
  Instagram,
  Facebook,
  MessageCircle,
  Link2,
  Camera,
} from 'lucide-react';
import { brand } from '@iheart/shared';
import { MotionButton, cn } from '@iheart/ui';
import { api, getAccessToken } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useNavigate } from 'react-router-dom';
import { XPlatformIcon } from './XPlatformIcon';

interface SocialLinks {
  facebook?: string;
  instagram?: string;
  twitter?: string;
  linkedin?: string;
  tiktok?: string;
  youtube?: string;
  email?: string;
  whatsapp?: string;
  website?: string;
  blog?: string;
}

interface Profile {
  publicId: string;
  name: string;
  email: string;
  avatarUrl: string | null;
  defaultTz: string;
  mfaEnabled: boolean;
  bio: string | null;
  phone: string | null;
  socialLinks: SocialLinks | null;
}

export function ProfileFlyout({ onClose }: { onClose: () => void }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { updateUser } = useAuth();
  const [editing, setEditing] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  async function handleAvatarUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    if (!file.type.match(/^image\/(jpeg|jpg|png|gif|webp)$/i)) return;
    if (file.size > 2 * 1024 * 1024) return;
    setAvatarUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const token = getAccessToken();
      const res = await fetch('/api/me/avatar', {
        method: 'POST',
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        body: form,
      });
      if (!res.ok) throw new Error('Upload failed');
      const result = await res.json();
      updateUser({ avatarUrl: result.avatarUrl });
      qc.invalidateQueries({ queryKey: ['profile'] });
    } catch {
      // silently fail
    } finally {
      setAvatarUploading(false);
    }
  }

  // Close on Escape key
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const profile = useQuery({
    queryKey: ['profile'],
    queryFn: () => api<Profile>('/me/profile'),
  });

  const data = profile.data;
  if (!data) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
        <div className="rounded-xl bg-white/75 p-8 backdrop-blur-2xl shadow-[0_4px_30px_rgba(0,0,0,0.04)] text-sm text-neutral-500">Loading...</div>
      </div>
    );
  }

  const initials = data.name
    .split(' ')
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="fixed inset-0 z-50 flex md:items-end md:justify-start" onClick={onClose} role="dialog" aria-modal="true" aria-label="User profile">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20" />

      {/* Panel */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative ml-0 w-full overflow-hidden rounded-t-2xl border border-white/30 bg-white/80 shadow-2xl backdrop-blur-2xl shadow-[0_4px_30px_rgba(0,0,0,0.05)] md:mb-4 md:ml-20 md:w-80 md:rounded-2xl"
      >
        {/* Header */}
        <div
          className="relative flex flex-col items-center px-5 pb-4 pt-10"
          style={{ backgroundImage: brand.gradient.primary }}
        >
          <button
            type="button"
            onClick={onClose}
            aria-label="Close profile"
            className="absolute right-3 top-3 rounded-lg p-1 text-white/70 transition-colors hover:text-white"
          >
            <X className="size-4" />
          </button>

          <button
            type="button"
            onClick={() => avatarInputRef.current?.click()}
            disabled={avatarUploading}
            className="group/avatar relative mb-3 flex size-20 items-center justify-center overflow-hidden rounded-full border-4 border-white/30 bg-white/20 text-xl font-bold text-white transition-opacity"
          >
            {data.avatarUrl ? (
              <img src={data.avatarUrl} alt="" className="size-full object-cover" />
            ) : (
              initials
            )}
            <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 opacity-0 transition-opacity group-hover/avatar:opacity-100">
              <Camera className="size-5 text-white" />
            </div>
            {avatarUploading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50">
                <div className="size-5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
              </div>
            )}
          </button>
          <input ref={avatarInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={handleAvatarUpload} />
          <h3 className="text-base font-semibold text-white">{data.name}</h3>
          <p className="text-xs text-white/70">{data.email}</p>
        </div>

        {/* Body */}
        <div className="max-h-[60vh] overflow-y-auto p-5">
          {editing ? (
            <EditForm
              profile={data}
              onSaved={() => {
                setEditing(false);
                qc.invalidateQueries({ queryKey: ['profile'] });
              }}
              onCancel={() => setEditing(false)}
            />
          ) : (
            <ViewMode
              profile={data}
              onEdit={() => setEditing(true)}
              onChat={() => {
                onClose();
                navigate('/chat');
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ViewMode({
  profile,
  onEdit,
  onChat,
}: {
  profile: Profile;
  onEdit: () => void;
  onChat: () => void;
}) {
  return (
    <div className="space-y-4">
      {/* Bio */}
      {profile.bio && (
        <div>
          <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
            Bio
          </h4>
          <p className="text-sm text-neutral-600">{profile.bio}</p>
        </div>
      )}

      {/* Contact */}
      <div>
        <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
          Contact
        </h4>
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-neutral-600">
            <Mail className="size-3.5 text-neutral-400" />
            {profile.email}
          </div>
          {profile.phone && (
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <Phone className="size-3.5 text-neutral-400" />
              {profile.phone}
            </div>
          )}
        </div>
      </div>

      {/* Social links */}
      {profile.socialLinks && Object.values(profile.socialLinks).some(Boolean) && (
        <div>
          <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
            Links
          </h4>
          <div className="flex flex-wrap gap-2">
            {profile.socialLinks.facebook && (
              <SocialChip
                icon={<Facebook className="size-3.5" />}
                label="Facebook"
                href={profile.socialLinks.facebook}
              />
            )}
            {profile.socialLinks.instagram && (
              <SocialChip
                icon={<Instagram className="size-3.5" />}
                label={profile.socialLinks.instagram}
                href={`https://instagram.com/${profile.socialLinks.instagram.replace('@', '')}`}
              />
            )}
            {profile.socialLinks.twitter && (
              <SocialChip
                icon={<XPlatformIcon className="size-3.5" />}
                label={profile.socialLinks.twitter}
                href={`https://x.com/${profile.socialLinks.twitter.replace('@', '')}`}
              />
            )}
            {profile.socialLinks.linkedin && (
              <SocialChip
                icon={<Linkedin className="size-3.5" />}
                label="LinkedIn"
                href={profile.socialLinks.linkedin}
              />
            )}
            {profile.socialLinks.email && (
              <SocialChip
                icon={<Mail className="size-3.5" />}
                label="Email"
                href={`mailto:${profile.socialLinks.email}`}
              />
            )}
            {profile.socialLinks.whatsapp && (
              <SocialChip
                icon={<MessageCircle className="size-3.5" />}
                label="WhatsApp"
                href={`https://wa.me/${profile.socialLinks.whatsapp.replace(/[^0-9]/g, '')}`}
              />
            )}
            {profile.socialLinks.website && (
              <SocialChip
                icon={<Globe className="size-3.5" />}
                label="Website"
                href={profile.socialLinks.website}
              />
            )}
            {profile.socialLinks.blog && (
              <SocialChip
                icon={<Link2 className="size-3.5" />}
                label="Blog"
                href={profile.socialLinks.blog}
              />
            )}
          </div>
        </div>
      )}

      {/* Info */}
      <div>
        <h4 className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-neutral-400">
          Info
        </h4>
        <div className="space-y-1.5 text-xs text-neutral-500">
          <p>Timezone: {profile.defaultTz}</p>
          <p>MFA: {profile.mfaEnabled ? 'Enabled' : 'Disabled'}</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-2">
        <MotionButton size="sm" variant="secondary" className="flex-1" onClick={onEdit}>
          <Pencil className="mr-1.5 size-3.5" />
          Edit profile
        </MotionButton>
        <MotionButton size="sm" className="flex-1" onClick={onChat}>
          <MessageCircle className="mr-1.5 size-3.5" />
          Chat
        </MotionButton>
      </div>
    </div>
  );
}

function SocialChip({
  icon,
  label,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  href: string;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-1 text-xs text-neutral-600 transition-colors hover:bg-neutral-50"
    >
      {icon}
      <span className="truncate">{label}</span>
    </a>
  );
}

function EditForm({
  profile,
  onSaved,
  onCancel,
}: {
  profile: Profile;
  onSaved: () => void;
  onCancel: () => void;
}) {
  const [bio, setBio] = useState(profile.bio ?? '');
  const [phone, setPhone] = useState(profile.phone ?? '');
  const [facebook, setFacebook] = useState(profile.socialLinks?.facebook ?? '');
  const [instagram, setInstagram] = useState(profile.socialLinks?.instagram ?? '');
  const [twitter, setTwitter] = useState(profile.socialLinks?.twitter ?? '');
  const [linkedin, setLinkedin] = useState(profile.socialLinks?.linkedin ?? '');
  const [contactEmail, setContactEmail] = useState(profile.socialLinks?.email ?? '');
  const [whatsapp, setWhatsapp] = useState(profile.socialLinks?.whatsapp ?? '');
  const [website, setWebsite] = useState(profile.socialLinks?.website ?? '');

  const update = useMutation({
    mutationFn: () =>
      api('/me/profile', {
        method: 'PATCH',
        body: {
          bio: bio.trim() || null,
          phone: phone.trim() || null,
          socialLinks: {
            facebook: facebook.trim() || undefined,
            instagram: instagram.trim() || undefined,
            twitter: twitter.trim() || undefined,
            linkedin: linkedin.trim() || undefined,
            email: contactEmail.trim() || undefined,
            whatsapp: whatsapp.trim() || undefined,
            website: website.trim() || undefined,
          },
        },
      }),
    onSuccess: onSaved,
  });

  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold text-neutral-700">Edit profile</h4>

      <div>
        <label className="mb-1 block text-[10px] font-medium text-neutral-500">Bio</label>
        <textarea
          rows={3}
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          maxLength={500}
          className="w-full resize-none rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-pink-400"
          placeholder="Tell us about yourself..."
        />
        <p className="mt-0.5 text-right text-[10px] text-neutral-400">{bio.length}/500</p>
      </div>

      <div>
        <label className="mb-1 block text-[10px] font-medium text-neutral-500">Phone</label>
        <input
          type="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-pink-400"
          placeholder="+27 82 123 4567"
        />
      </div>

      <div className="space-y-2">
        <label className="block text-[10px] font-medium text-neutral-500">Links</label>
        <div className="flex items-center gap-2">
          <Facebook className="size-4 shrink-0 text-neutral-400" />
          <input type="text" value={facebook} onChange={(e) => setFacebook(e.target.value)}
            className="flex-1 rounded-lg border border-border px-3 py-1.5 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            placeholder="Facebook URL" />
        </div>
        <div className="flex items-center gap-2">
          <Instagram className="size-4 shrink-0 text-neutral-400" />
          <input type="text" value={instagram} onChange={(e) => setInstagram(e.target.value)}
            className="flex-1 rounded-lg border border-border px-3 py-1.5 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            placeholder="@handle" />
        </div>
        <div className="flex items-center gap-2">
          <XPlatformIcon className="size-4 shrink-0 text-neutral-400" />
          <input type="text" value={twitter} onChange={(e) => setTwitter(e.target.value)}
            className="flex-1 rounded-lg border border-border px-3 py-1.5 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            placeholder="@handle" />
        </div>
        <div className="flex items-center gap-2">
          <Linkedin className="size-4 shrink-0 text-neutral-400" />
          <input type="text" value={linkedin} onChange={(e) => setLinkedin(e.target.value)}
            className="flex-1 rounded-lg border border-border px-3 py-1.5 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            placeholder="LinkedIn URL" />
        </div>
        <div className="flex items-center gap-2">
          <Mail className="size-4 shrink-0 text-neutral-400" />
          <input type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)}
            className="flex-1 rounded-lg border border-border px-3 py-1.5 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            placeholder="contact@email.com" />
        </div>
        <div className="flex items-center gap-2">
          <MessageCircle className="size-4 shrink-0 text-neutral-400" />
          <input type="tel" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)}
            className="flex-1 rounded-lg border border-border px-3 py-1.5 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            placeholder="+27 82 123 4567" />
        </div>
        <div className="flex items-center gap-2">
          <Globe className="size-4 shrink-0 text-neutral-400" />
          <input type="url" value={website} onChange={(e) => setWebsite(e.target.value)}
            className="flex-1 rounded-lg border border-border px-3 py-1.5 text-sm outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100"
            placeholder="https://..." />
        </div>
      </div>

      <div className="flex gap-2 pt-1">
        <MotionButton
          size="sm"
          className="flex-1"
          onClick={() => update.mutate()}
          disabled={update.isPending}
        >
          <Check className="mr-1.5 size-3.5" />
          {update.isPending ? 'Saving...' : 'Save'}
        </MotionButton>
        <MotionButton size="sm" variant="secondary" className="flex-1" onClick={onCancel}>
          Cancel
        </MotionButton>
      </div>
    </div>
  );
}
